import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import styled from 'styled-components';
import type {
  Asset,
  AuthenticatedUser,
  CommentMatch as CommentMatchType,
} from '@frameio/graphql-client/generated';
import { useFormattedRelativeDate } from '@frameio/utils/hooks';
import { Avatar, Box, Text } from '@frameio/vapor';
import type { ThemeColorsBackground } from '@frameio/vapor-theme';
import { HighlightText } from '../../../../../../shared/components';
import { useQuickSearchModal } from '../../../../../../shared/contexts';
import { useQuickSearchSupportedAssetMediaV2Fragment } from '../../../../../../shared/gql/generated/QuickSearchSupportedAssetMediaV2';
import type { OnMatchClickHandler } from '../../../../../../shared/types';
import { getAssetResultItemLink } from '../../../../../../shared/utils';
import { MatchCard, MatchCardsContainer } from './shared';

const AVATAR_SIZE = 24;

type CommentMatchItemProps = {
  assetId: string;
  commentMatch: CommentMatchType;
  onClick: (commentId: string, assetData: Asset) => void;
  onMatchClick?: OnMatchClickHandler;
};

const CommentMatchItem = ({
  assetId,
  commentMatch,
  onClick,
  onMatchClick,
}: CommentMatchItemProps) => {
  const { data: assetData } =
    useQuickSearchSupportedAssetMediaV2Fragment(assetId);

  const comment = commentMatch.comment;
  const snippet = commentMatch.snippet;
  const terms = commentMatch.terms as string[];

  const formattedDate = useFormattedRelativeDate({
    date: new Date(comment?.insertedAt ?? ''),
    truncate: true,
    showSuffix: false,
  });

  if (!comment || !comment.id) return null;

  const hasHighlights = Boolean(terms.length);
  const displayText = snippet || comment?.text || '';
  const ownerName = comment?.owner?.name;

  return (
    <MatchCard
      key={`comment-match-${comment?.id}`}
      aria-label={`View comment by ${ownerName} in context`}
      onClick={() => {
        onMatchClick?.(commentMatch.__typename ?? 'CommentMatch');
        onClick(comment.id, assetData as Asset);
      }}
      role="button"
      tabIndex={0}
    >
      <Box display="flex" gap={8}>
        <Avatar
          alt={ownerName ?? ''}
          background={comment?.owner?.avatarColor as ThemeColorsBackground}
          size={AVATAR_SIZE}
          src={
            (comment?.owner as AuthenticatedUser)?.profileImage?.image64 || ''
          }
          style={{ flexShrink: 0 }}
        />

        <Box style={{ minWidth: 0, flex: 1 }}>
          <CommentMeta>
            <Box
              alignItems="center"
              display="flex"
              gap={4}
              style={{ minWidth: 0 }}
            >
              {ownerName && (
                <Text
                  color="primary"
                  enableTooltip={true}
                  fontWeight="medium"
                  style={{ minWidth: 0 }}
                  truncate={true}
                  variant="secondary"
                >
                  {ownerName}
                </Text>
              )}
              <Text
                color="tertiary"
                style={{ flexShrink: 0 }}
                variant="caption"
              >
                {formattedDate}
              </Text>
            </Box>
          </CommentMeta>

          {hasHighlights ? (
            <HighlightText
              color="secondary"
              highlights={terms}
              text={displayText}
              truncate={false}
              variant="secondary"
            />
          ) : (
            <Text color="secondary" variant="secondary">
              {displayText}
            </Text>
          )}
        </Box>
      </Box>
    </MatchCard>
  );
};

type CommentMatchProps = {
  commentMatches: CommentMatchType[];
  assetId: string;
  onMatchClick?: OnMatchClickHandler;
};

export const CommentMatch = ({
  commentMatches,
  assetId,
  onMatchClick,
}: CommentMatchProps) => {
  const router = useRouter();
  const { closeModal } = useQuickSearchModal();

  const handleCommentClick = useCallback(
    (commentId: string, assetData: Asset) => {
      const href = getAssetResultItemLink(assetData, {
        commentId,
      });
      router.push(href);
      closeModal({ navigatingTo: href });
    },
    [router, closeModal]
  );

  if (!commentMatches || commentMatches.length === 0) {
    return null;
  }

  return (
    <MatchCardsContainer>
      {commentMatches.map((commentMatch) =>
        commentMatch.comment?.id ? (
          <CommentMatchItem
            key={commentMatch.comment.id}
            assetId={assetId}
            commentMatch={commentMatch}
            onClick={handleCommentClick}
            onMatchClick={onMatchClick}
          />
        ) : null
      )}
    </MatchCardsContainer>
  );
};

const CommentMeta = styled.div`
  flex: 1;
  min-width: 0;
`;
